import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../../Context/authContext';
import Login from '../Auth/Login';
import SignUp from '../Auth/SignUp';
import ForgotPassword from '../Auth/ForgotPassword';
import UpdatePassword from '../Auth/UpdatePassword';
import AcceptTeamInvite from '../AcceptTeamInvite';
import Dashboard from '../Dashboard';
import AdminDashboard from '../AdminDashboard';
import IdeasReview from '../IdeasReview';
import IdeaDetail from '../IdeaDetail';
import Engagements from '../Engagements';
import EngagementDetail from '../EngagementDetail';
import Calendar from '../Calendar';
import Tasks from '../Tasks';
import BrandAssets from '../BrandAssets';
import Invoices from '../Invoices';
import SponsorshipHistory from '../SponsorshipHistory';
import Settings from '../Settings';
import Profile from '../Profile';
import Mentors from '../Mentors';
import Students from '../Students';
import Ideas from '../Ideas';
import Sponsors from '../Sponsors';
import CorporateSponsors from '../CorporateSponsors';
import EcosystemPartners from '../EcosystemPartners';
import Donors from '../Donors';
import Investors from '../Investors';
import Volunteers from '../Volunteers';
import UserDetail from '../UserDetail';
import EmailTemplates from '../EmailTemplates';
import Events from '../Events';
import EventDetail from '../EventDetail';
import EventRegistrationDetail from '../EventRegistrationDetail';
import FAQs from '../FAQs';
import Testimonials from '../Testimonials';
import CompleteRegistration from '../CompleteRegistration';
import Content from '../Content';
import StageQuestions from '../StageQuestions';
import Configuration from '../Configuration';
import SideNavigation from '../../Components/SideNavigation';
import { hasAccessToRoute, getRoutesForRole } from '../../Utils/roleRoutes';
import MentorDashboard from '../MentorDashboard';
import MentorEvents from '../MentorEvents';
import DonorHome from '../DonorHome';
import DonorDonations from '../DonorDonations';
import MasterData from '../../pages/admin/MasterData';
import Unauthorized from '../../pages/Unauthorized';
import AdminGuard from '../../Guards/AdminGuard';

// Component mapping for all routes
const componentMap = {
  '/': Dashboard,
  '/admin-dashboard': AdminDashboard,
  '/tasks': Tasks,
  '/ideas-review': IdeasReview,
  '/engagements': Engagements,
  '/calendar': Calendar,
  '/invoices': Invoices,
  '/brand-assets': BrandAssets,
  '/sponsorship-history': SponsorshipHistory,
  '/settings': Settings,
  '/profile': Profile,
  '/mentors': Mentors,
  '/students': Students,
  '/ideas': Ideas,
  '/questions': StageQuestions,
  '/sponsors': Sponsors,
  '/corporate-sponsors': CorporateSponsors,
  '/ecosystem-partners': EcosystemPartners,
  '/donors': Donors,
  '/investors': Investors,
  '/volunteers': Volunteers,
  '/email-templates': EmailTemplates,
  '/events': Events,
  '/faqs': FAQs,
  '/testimonials': Testimonials,
  '/complete-registration': CompleteRegistration,
  '/mentor-dashboard': MentorDashboard,
  '/mentor-events': MentorEvents,
  '/donor-home': DonorHome,
  '/donor-donations': DonorDonations,
  '/content': Content,
  '/configuration': Configuration,
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Role-based Protected Route Component
const RoleProtectedRoute = ({ children, path }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Special handling for mentors with pending/submitted status or incomplete profile
  if (user?.role === 'mentor') {
    const mentorStatus = user?.mentor_status;
    const isProfileCompleted = user?.is_profile_completed;
    const needsCompleteRegistration = mentorStatus === "pending" || mentorStatus === "submitted" || isProfileCompleted === false;
    
    // If mentor needs to complete registration, only allow access to complete-registration
    if (needsCompleteRegistration && path !== "/complete-registration") {
      return <Navigate to="/complete-registration" replace />;
    }
    
    // If mentor has completed registration, don't allow access to complete-registration
    if (!needsCompleteRegistration && path === "/complete-registration") {
      return <Navigate to="/mentor-dashboard" replace />;
    }
  }

  // Special handling for partners (ecosystem_partner, corporate_sponsor) and investors with incomplete profile
  if (user?.role === 'ecosystem_partner' || user?.role === 'corporate_sponsor' || user?.role === 'investor') {
    const isProfileCompleted = user?.is_profile_completed;
    const needsCompleteRegistration = isProfileCompleted === false;
    
    // If partner/investor needs to complete registration, only allow access to complete-registration
    if (needsCompleteRegistration && path !== "/complete-registration") {
      return <Navigate to="/complete-registration" replace />;
    }
    
    // If partner/investor has completed registration, don't allow access to complete-registration
    if (!needsCompleteRegistration && path === "/complete-registration") {
      return <Navigate to="/" replace />;
    }
  }

  // Special handling for students with incomplete profile
  if (user?.role === 'student') {
    const isProfileCompleted = user?.is_profile_completed;
    const needsCompleteRegistration = isProfileCompleted === false;
    
    // If student needs to complete registration, only allow access to complete-registration
    if (needsCompleteRegistration && path !== "/complete-registration") {
      return <Navigate to="/complete-registration" replace />;
    }
    
    // If student has completed registration, don't allow access to complete-registration
    if (!needsCompleteRegistration && path === "/complete-registration") {
      return <Navigate to="/" replace />;
    }
  }

  // Check if user's role has access to this route
  if (!hasAccessToRoute(user?.role, path)) {
    // Redirect to role-specific home instead of generic "/"
    const userRole = user?.role;
    
    // Check if mentor needs to complete registration
    if (userRole === 'mentor') {
      const mentorStatus = user?.mentor_status;
      const isProfileCompleted = user?.is_profile_completed;
      if (mentorStatus === "pending" || mentorStatus === "submitted" || isProfileCompleted === false) {
        return <Navigate to="/complete-registration" replace />;
      }
      return <Navigate to="/mentor-dashboard" replace />;
    }
    
    // Check if partner (ecosystem_partner, corporate_sponsor) or investor needs to complete registration
    if (userRole === 'ecosystem_partner' || userRole === 'corporate_sponsor' || userRole === 'investor') {
      const isProfileCompleted = user?.is_profile_completed;
      if (isProfileCompleted === false) {
        return <Navigate to="/complete-registration" replace />;
      }
      return <Navigate to="/" replace />;
    }
    
    // Check if student needs to complete registration
    if (userRole === 'student') {
      const isProfileCompleted = user?.is_profile_completed;
      if (isProfileCompleted === false) {
        return <Navigate to="/complete-registration" replace />;
      }
      return <Navigate to="/" replace />;
    }
    
    switch (userRole) {
      case 'admin':
        return <Navigate to="/admin-dashboard" replace />;
      case 'donor':
        return <Navigate to="/donor-home" replace />;
      default:
        return <Navigate to="/" replace />;
    }
  }

  return children;
};

// Component to redirect based on user role
const RoleBasedRedirect = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  const userRole = user?.role;
  
  // Check if mentor needs to complete registration
  if (userRole === 'mentor') {
    const mentorStatus = user?.mentor_status;
    const isProfileCompleted = user?.is_profile_completed;
    if (mentorStatus === "pending" || mentorStatus === "submitted" || isProfileCompleted === false) {
      return <Navigate to="/complete-registration" replace />;
    }
    return <Navigate to="/mentor-dashboard" replace />;
  }
  
  // Check if partner (ecosystem_partner, corporate_sponsor) or investor needs to complete registration
  if (userRole === 'ecosystem_partner' || userRole === 'corporate_sponsor' || userRole === 'investor') {
    const isProfileCompleted = user?.is_profile_completed;
    if (isProfileCompleted === false) {
      return <Navigate to="/complete-registration" replace />;
    }
    return <Navigate to="/" replace />;
  }
  
  // Check if student needs to complete registration
  if (userRole === 'student') {
    const isProfileCompleted = user?.is_profile_completed;
    if (isProfileCompleted === false) {
      return <Navigate to="/complete-registration" replace />;
    }
    return <Navigate to="/" replace />;
  }

  if (userRole === 'donor') {
    return <Navigate to="/donor-home" replace />;
  }
  
  switch (userRole) {
    case 'admin':
    return <Navigate to="/admin-dashboard" replace />;
    default:
    return <Navigate to="/" replace />;
  }
};

// Public Route Component (redirects to home if already authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (isAuthenticated) {
    // Redirect to role-specific home
    const userRole = user?.role;
    
    // Check if mentor needs to complete registration
    if (userRole === 'mentor') {
      const mentorStatus = user?.mentor_status;
      const isProfileCompleted = user?.is_profile_completed;
      if (mentorStatus === "pending" || mentorStatus === "submitted" || isProfileCompleted === false) {
        return <Navigate to="/complete-registration" replace />;
      }
      return <Navigate to="/mentor-dashboard" replace />;
    }
    
    // Check if partner or investor needs to complete registration
    if (userRole === 'ecosystem_partner' || userRole === 'corporate_sponsor' || userRole === 'investor') {
      const isProfileCompleted = user?.is_profile_completed;
      if (isProfileCompleted === false) {
        return <Navigate to="/complete-registration" replace />;
      }
      return <Navigate to="/" replace />;
    }
    
    // Check if student needs to complete registration
    if (userRole === 'student') {
      const isProfileCompleted = user?.is_profile_completed;
      if (isProfileCompleted === false) {
        return <Navigate to="/complete-registration" replace />;
      }
      return <Navigate to="/" replace />;
    }

    if (userRole === 'donor') {
      return <Navigate to="/donor-home" replace />;
    }
    
    switch (userRole) {
      case 'admin':
        return <Navigate to="/admin-dashboard" replace />;
      default:
        return <Navigate to="/" replace />;
    }
  }

  return children;
};

// Render route based on role
const renderRoute = (route) => {
  const Component = componentMap[route.path];
  if (!Component) return null;

  return (
    <Route
      key={route.path}
      path={route.path}
      element={
        <ProtectedRoute>
          <RoleProtectedRoute path={route.path}>
            <SideNavigation>
              <Component />
            </SideNavigation>
          </RoleProtectedRoute>
        </ProtectedRoute>
      }
    />
  );
};

const MainLayout = () => {
  // But access will be controlled by RoleProtectedRoute
  // Collect unique paths from all roles
  const allUniqueRoutes = new Set();
  
  // Add all routes from all roles
  getRoutesForRole('student').forEach(route => allUniqueRoutes.add(route.path));
  getRoutesForRole('mentor').forEach(route => allUniqueRoutes.add(route.path));
  getRoutesForRole('donor').forEach(route => allUniqueRoutes.add(route.path));
  getRoutesForRole('ecosystem_partner').forEach(route => allUniqueRoutes.add(route.path));
  getRoutesForRole('corporate_sponsor').forEach(route => allUniqueRoutes.add(route.path));
  getRoutesForRole('sponsor').forEach(route => allUniqueRoutes.add(route.path));
  getRoutesForRole('investor').forEach(route => allUniqueRoutes.add(route.path));
  getRoutesForRole('admin').forEach(route => allUniqueRoutes.add(route.path));
  getRoutesForRole('program_team').forEach(route => allUniqueRoutes.add(route.path));

  // Routes that require custom guards/wrappers (don’t render via the generic map)
  const excludedPaths = new Set(['/admin/master-data']);
  const allRoutes = Array.from(allUniqueRoutes)
    .filter((path) => !excludedPaths.has(path))
    .map(path => ({ path }));

  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute>
            <SignUp />
          </PublicRoute>
        }
      />
      {/* Co-founder invite acceptance — open to everyone (don't bounce logged-in users).
          APP_URL includes /login, so the emailed link resolves to /login/accept-team-invite. */}
      <Route path="/accept-team-invite" element={<AcceptTeamInvite />} />
      <Route path="/login/accept-team-invite" element={<AcceptTeamInvite />} />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />
      <Route
        path="/update-password"
        element={
          <PublicRoute>
            <UpdatePassword />
          </PublicRoute>
        }
      />
      {/* Password reset link from email often uses /login/update-password */}
      <Route
        path="/login/update-password"
        element={
          <PublicRoute>
            <UpdatePassword />
          </PublicRoute>
        }
      />

      {/* Protected Role-based Routes */}
      {allRoutes.map((route) => renderRoute(route))}

      {/* Admin-only: Master Data */}
      <Route
        path="/admin/master-data"
        element={
          <AdminGuard>
            <SideNavigation>
              <MasterData />
            </SideNavigation>
          </AdminGuard>
        }
      />

      {/* Unauthorized */}
      <Route
        path="/unauthorized"
        element={
          <ProtectedRoute>
            <SideNavigation>
              <Unauthorized />
            </SideNavigation>
          </ProtectedRoute>
        }
      />

      {/* Profile Route - Available to all authenticated users */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <SideNavigation>
              <Profile />
            </SideNavigation>
          </ProtectedRoute>
        }
      />

      {/* User Detail Route - Available to all authenticated users */}
      <Route
        path="/users/:id"
        element={
          <ProtectedRoute>
            <SideNavigation>
              <UserDetail />
            </SideNavigation>
          </ProtectedRoute>
        }
      />

      {/* Event Detail Route - Available to all authenticated users */}
      <Route
        path="/events/:id"
        element={
          <ProtectedRoute>
            <SideNavigation>
              <EventDetail />
            </SideNavigation>
          </ProtectedRoute>
        }
      />

      {/* Event Registration Detail Route - Available to all authenticated users */}
      <Route
        path="/event-registrations/:id"
        element={
          <ProtectedRoute>
            <SideNavigation>
              <EventRegistrationDetail />
            </SideNavigation>
          </ProtectedRoute>
        }
      />

      {/* Idea Detail Route - Available to all authenticated users */}
      <Route
        path="/ideas/:id"
        element={
          <ProtectedRoute>
            <SideNavigation>
              <IdeaDetail />
            </SideNavigation>
          </ProtectedRoute>
        }
      />

      {/* Engagement Detail Route - Available to all authenticated users */}
      <Route
        path="/engagements/:id"
        element={
          <ProtectedRoute>
            <SideNavigation>
              <EngagementDetail />
            </SideNavigation>
          </ProtectedRoute>
        }
      />

      {/* Default route - redirect to role-specific dashboard */}
      <Route 
        path="*" 
        element={
          <ProtectedRoute>
            <RoleBasedRedirect />
          </ProtectedRoute>
        } 
      />
    </Routes>
  );
};

export default MainLayout;
